import {
    Box,
    Button,
    Collapse,
    Group,
    Modal,
    Stack,
    Text,
} from '@mantine/core';
import {
    CONFIRM_MODAL_MODES,
    ConfirmModalMode,
    useTodoModalStore,
} from '@/stores/todoModalStore';
import { useState } from 'react';
import {
    IconChevronDown,
    IconChevronUp,
    IconInfoCircle,
} from '@tabler/icons-react';
import { Trans, useTranslation } from 'react-i18next';
import { getDateLocale } from '@/common/utils/i18nUtils';

interface TodoConfirmModalProps {
    opened: boolean;
    onClose: () => void;
    canCancelTodo: boolean;
}

export const TodoConfirmModal = ({
    opened,
    onClose,
    canCancelTodo,
}: TodoConfirmModalProps) => {
    const { t } = useTranslation('todo');
    const [showDetail, setShowDetail] = useState(false);
    const { modalMode, targetDate, actions, changeModalMode } =
        useTodoModalStore();

    const getActionText = ({
        mode = modalMode,
    }: { mode?: ConfirmModalMode | null } = {}): string => {
        switch (mode) {
            case 'cancel':
                return t('component.confirm_modal.actions.cancel');
            case 'removePlanner':
                return t('component.confirm_modal.actions.remove_planner');
            case 'delete':
                return t('component.confirm_modal.actions.delete');
            default:
                return '';
        }
    };

    const getActionDetail = ({
        mode = modalMode,
    }: { mode?: ConfirmModalMode | null } = {}): string => {
        switch (mode) {
            case 'cancel':
                return t('component.confirm_modal.details.cancel');
            case 'removePlanner':
                return t('component.confirm_modal.details.remove_planner');
            case 'delete':
                return t('component.confirm_modal.details.delete');
            default:
                return '';
        }
    };

    const getDescription: () => React.ReactNode = () => {
        switch (modalMode) {
            case 'cancel':
                return (
                    <Stack gap={0}>
                        <Text size="sm">
                            {t(
                                'todo:component.confirm_modal.descriptions.cancel_1'
                            )}
                        </Text>
                        {targetDate && (
                            <Group gap={0} fz="sm">
                                <Trans
                                    i18nKey="todo:component.confirm_modal.descriptions.cancel_2"
                                    components={[
                                        <Text size="sm" span key="light" />,
                                        <Text fw="bold" size="sm" key="bold" />,
                                        <Text size="sm" span key="light" />,
                                    ]}
                                    values={{
                                        date: new Date(
                                            targetDate
                                        ).toLocaleDateString(getDateLocale(), {
                                            month: 'long',
                                            day: 'numeric',
                                        }),
                                    }}
                                />
                            </Group>
                        )}
                    </Stack>
                );
            case 'removePlanner':
                return (
                    <Stack gap={0}>
                        {targetDate && (
                            <>
                                <Group gap={0}>
                                    <Trans
                                        i18nKey="todo:component.confirm_modal.descriptions.remove_planner_1"
                                        components={[
                                            <Text size="sm" span key="light" />,
                                            <Text
                                                fw="bold"
                                                size="sm"
                                                key="bold"
                                            />,
                                        ]}
                                        values={{
                                            date: new Date(
                                                targetDate
                                            ).toLocaleDateString(
                                                getDateLocale(),
                                                {
                                                    month: 'long',
                                                    day: 'numeric',
                                                }
                                            ),
                                        }}
                                    />
                                </Group>
                                <Text size="sm">
                                    {t(
                                        'todo:component.confirm_modal.descriptions.remove_planner_2'
                                    )}
                                </Text>
                            </>
                        )}
                    </Stack>
                );
            case 'delete':
                return (
                    <Stack gap={0}>
                        <Text size="sm">
                            {t('component.confirm_modal.descriptions.delete_1')}
                        </Text>
                        {targetDate && (
                            <Group gap={0}>
                                <Trans
                                    span
                                    i18nKey="todo:component.confirm_modal.descriptions.delete_2"
                                    components={[
                                        <Text size="sm" key="light" />,

                                        <Text fw="bold" size="sm" key="bold" />,
                                    ]}
                                    values={{
                                        date: new Date(
                                            targetDate
                                        ).toLocaleDateString(getDateLocale(), {
                                            month: 'long',
                                            day: 'numeric',
                                        }),
                                    }}
                                />
                            </Group>
                        )}
                    </Stack>
                );
            default:
                return '';
        }
    };

    const handleConfirm = async (): Promise<void> => {
        onClose();

        switch (modalMode) {
            case 'cancel':
                await actions.handleCancelTodo();
                break;
            case 'removePlanner':
                await actions.handleRemovePlanner();
                break;
            case 'delete':
                await actions.handleDeleteTodo();
                break;
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={getActionText()}
            centered
            radius="lg"
        >
            <Stack>
                {getDescription()}
                <Group justify="space-between">
                    <div
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowDetail(!showDetail)}
                    >
                        <Group gap={4}>
                            <IconInfoCircle size={14} />
                            <Text size="xs">
                                {t(
                                    'component.confirm_modal.other.info_tooltip'
                                )}
                            </Text>
                            {showDetail ? (
                                <IconChevronUp size={14} color="gray" />
                            ) : (
                                <IconChevronDown size={14} color="gray" />
                            )}
                        </Group>
                    </div>
                    <Group justify="flex-end" gap={4}>
                        {/* secondary action button */}
                        {modalMode === 'removePlanner' && (
                            <Button
                                variant="outline"
                                onClick={() => changeModalMode('delete')}
                                size="sm"
                                radius="md"
                            >
                                {getActionText({ mode: 'delete' })}
                            </Button>
                        )}
                        {modalMode === 'delete' && canCancelTodo && (
                            <Button
                                variant="outline"
                                onClick={() => changeModalMode('removePlanner')}
                                size="sm"
                                radius="md"
                            >
                                {getActionText({ mode: 'removePlanner' })}
                            </Button>
                        )}

                        {/* main action button */}
                        <Button
                            variant="filled"
                            size="sm"
                            radius="md"
                            onClick={() => void handleConfirm()}
                        >
                            {getActionText()}
                        </Button>
                    </Group>
                </Group>

                <Collapse in={showDetail}>
                    <Box bg="gray.1" p="md" style={{ borderRadius: '.5rem' }}>
                        <Stack gap="lg">
                            {CONFIRM_MODAL_MODES.map(mode => (
                                <Stack key={mode} gap={4}>
                                    <Group gap={8}>
                                        <Text fw={600} fz="sm">
                                            {getActionText({ mode })}
                                        </Text>
                                        {mode === modalMode && (
                                            <Text fz="xs" c="blue.7">
                                                {t(
                                                    'component.confirm_modal.other.current_option'
                                                )}
                                            </Text>
                                        )}
                                    </Group>
                                    <Text
                                        fz="xs"
                                        style={{ whiteSpace: 'pre-line' }}
                                    >
                                        {getActionDetail({ mode })}
                                    </Text>
                                </Stack>
                            ))}
                        </Stack>
                    </Box>
                </Collapse>
            </Stack>
        </Modal>
    );
};
